import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../../../api/client';

function fmtTime(s) {
  if (!s) return '—';
  return s.replace('T', ' ').replace(/\.\d+/, '').replace(/Z$/, '');
}

function DirectionBadge({ direction }) {
  const colorMap = {
    caller:     { bg: '#5AB6FF', label: 'Caller' },
    agent:      { bg: '#F5A623', label: 'Agent' },
    no_connect: { bg: '#9CA3AF', label: 'No connect' },
    unknown:    { bg: '#9CA3AF', label: 'Unknown' },
  };
  const c = colorMap[direction] || colorMap.unknown;
  return (
    <span
      style={{
        padding: '2px 8px', borderRadius: 999,
        fontSize: 11, fontWeight: 600,
        background: c.bg, color: '#fff',
      }}
    >
      {c.label}
    </span>
  );
}

export default function CallTracePanel({ linkedid, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['cdr-trace', linkedid],
    queryFn: () => reportsApi.cdrTrace(linkedid),
    enabled: !!linkedid,
    staleTime: 60_000,
  });

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/30 z-40"
      />
      {/* Side panel */}
      <div className="fixed top-0 right-0 h-full w-[480px] max-w-[100vw] z-50 bg-surface-panel dark:bg-surface-dark-panel border-l border-surface-border dark:border-surface-dark-border shadow-2xl flex flex-col">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center justify-between">
          <div>
            <div className="text-xs text-surface-muted">Call trace</div>
            <div className="text-sm font-mono">{linkedid}</div>
          </div>
          <button
            onClick={onClose}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle"
          >
            ✕ Close
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4 text-sm">
          {isLoading && <div className="text-surface-muted">Loading CEL events…</div>}
          {isError && (
            <div className="text-red-600 dark:text-red-300">
              {error?.detail || error?.message || 'Failed to load trace.'}
            </div>
          )}
          {data && (
            <>
              {/* Summary */}
              <div className="mb-4 p-3 rounded-thin bg-surface-subtle dark:bg-surface-dark-subtle">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-surface-muted">Disconnect direction</span>
                  <DirectionBadge direction={data.disconnect_direction} />
                </div>
                {data.disconnect_channel && (
                  <div className="text-xs font-mono mt-1 break-all opacity-80">
                    {data.disconnect_channel}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div>
                    <div className="text-surface-muted">Answered at</div>
                    <div className="font-mono">{fmtTime(data.answer_at)}</div>
                  </div>
                  <div>
                    <div className="text-surface-muted">Hung up at</div>
                    <div className="font-mono">{fmtTime(data.hangup_at)}</div>
                  </div>
                </div>
              </div>

              {/* Legs */}
              <div className="mb-4">
                <div className="text-xs uppercase tracking-wide text-surface-muted mb-1">Legs</div>
                <div className="space-y-1">
                  {(data.legs || []).map((leg, i) => (
                    <div key={i} className="border border-surface-border dark:border-surface-dark-border rounded-thin p-2">
                      <div className="flex items-center justify-between">
                        <DirectionBadge direction={leg.role === 'local-helper' ? 'unknown' : leg.role} />
                        <span className="text-xs text-surface-muted">{leg.cid_num || '—'}</span>
                      </div>
                      <div className="text-xs font-mono mt-1 break-all">{leg.channame}</div>
                      <div className="text-xs text-surface-muted mt-1">
                        start: {fmtTime(leg.start_at)} · answered: {fmtTime(leg.answer_at)} · end: {fmtTime(leg.end_at)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Event ladder */}
              <div>
                <div className="text-xs uppercase tracking-wide text-surface-muted mb-1">
                  Events ({(data.events || []).length})
                </div>
                <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
                      <tr>
                        <th className="text-left px-2 py-1 font-semibold">Time</th>
                        <th className="text-left px-2 py-1 font-semibold">Type</th>
                        <th className="text-left px-2 py-1 font-semibold">Channel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.events || []).map((ev, i) => (
                        <tr key={i} className="border-t border-surface-border dark:border-surface-dark-border">
                          <td className="px-2 py-1 font-mono text-[10px] whitespace-nowrap">{fmtTime(ev.at)}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{ev.type}</td>
                          <td className="px-2 py-1 font-mono text-[10px] break-all">{ev.channame}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
